import {
  Heading,
  UserCompanySelector,
  UserCompanySelectorOption,
} from '@assembly-js/design-system';
import { createSelector } from '@reduxjs/toolkit';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorMessage } from 'src/components/ErrorMessage';
import { useAppSelector } from 'src/hooks/useStore';
import { useGetPortalConfigQuery } from 'src/services/api';
import { RootState } from 'src/store';
import {
  Company,
  selectAllCompaniesMap,
  selectClientsMap,
} from 'src/store/clients/types';
import { zIndex } from 'src/theme/zindex';

export type RecipientType = 'client' | 'company' | 'internalUser';

export const selectRecipientComboData = createSelector(
  [
    (state: RootState) => state.clients.activeClients,
    selectAllCompaniesMap,
    (_, recipientTypes: RecipientType[]) => recipientTypes,
    (_, _r, excludedRecipients: { clientId: string; companyId: string }[]) =>
      excludedRecipients,
    (_, _r, _r2, isCompaniesEnabled: boolean) => isCompaniesEnabled,
  ],
  (
    clients,
    companiesMap,
    recipientTypes,
    excludedRecipients,
    isCompaniesEnabled,
  ) => {
    const clientOptions: UserCompanySelectorOption[] = [];
    const companyOptions: UserCompanySelectorOption[] = [];
    // Companies referenced by expanded client options — always fed to
    // UserCompanySelector's companies map for badges, including placeholder /
    // archived memberships. When those companies must not be selectable
    // company-only rows (Billing with companies enabled), their ids are also
    // returned in hiddenCompanyOptionIds for a filterOption override.
    const referencedCompanyIds = new Set<string>();
    const seenCompanyOptionIds = new Set<string>();
    const hiddenCompanyOptionIds = new Set<string>();

    const isInactiveCompany = (company: Company) =>
      company.fields.isPlaceholder || company.entityStatus === 'ARCHIVED';

    const pushCompanyOption = (company: Company) => {
      if (seenCompanyOptionIds.has(company.id)) {
        return;
      }
      seenCompanyOptionIds.add(company.id);
      companyOptions.push({
        value: company.id,
        label: company.fields.name || '',
        type: 'company',
        companyId: company.id,
        avatarSrc: company.fields.avatarImageURL,
        avatarFallbackColor: company.fields.fallbackColor,
      });
    };

    if (recipientTypes.includes('client')) {
      clients
        .sort((a, b) => b.createdDate.localeCompare(a.createdDate))
        .forEach((c) => {
          c.fields.companyIds?.forEach((companyId) => {
            const company = companiesMap.get(companyId);
            if (company) {
              if (
                excludedRecipients.some(
                  (r) => r.clientId === c.id && r.companyId === company.id,
                )
              ) {
                return;
              }
              clientOptions.push({
                value: c.id,
                label: `${c.fields.givenName} ${c.fields.familyName}`,
                companyId: company.id,
                type: 'client',
                avatarSrc: c.fields.avatarImageUrl,
                avatarFallbackColor: c.fields.fallbackColor,
              });
              referencedCompanyIds.add(company.id);
            }
          });
        });

      if (isCompaniesEnabled) {
        Array.from(companiesMap.values())
          .sort((a, b) => b.createdDate.localeCompare(a.createdDate))
          .forEach((company) => {
            if (isInactiveCompany(company)) {
              return;
            }

            pushCompanyOption(company);
          });
      }

      // Always backfill referenced companies for badges. companyOptions also
      // drives selectable company rows when Billing includes 'company', so mark
      // placeholder/archived refs as hidden in that case — RecipientSelector
      // filters them out of company-only choices via filterOption.
      const companyRowsSelectable =
        isCompaniesEnabled && recipientTypes.includes('company');
      referencedCompanyIds.forEach((companyId) => {
        const company = companiesMap.get(companyId);
        if (!company) {
          return;
        }
        pushCompanyOption(company);
        if (companyRowsSelectable && isInactiveCompany(company)) {
          hiddenCompanyOptionIds.add(company.id);
        }
      });
    }

    const internalUserOptions: UserCompanySelectorOption[] | undefined =
      undefined;

    if (recipientTypes.includes('internalUser')) {
      // TODO: Add internal user options
    }
    return {
      clientOptions,
      companyOptions,
      internalUserOptions,
      hiddenCompanyOptionIds: Array.from(hiddenCompanyOptionIds),
    };
  },
);

/**
 * This selector is used to get the controlled value for the recipient selector.
 * This is needed in cases where the selected values is determined by the parent component
 * such as in ShareForms when submitting Select All Clients
 */
export const selectRecipientValues = createSelector(
  [
    selectClientsMap,
    selectAllCompaniesMap,
    (
      _,
      selectedRecipients: {
        clientId: string;
        companyId: string;
        type: RecipientType;
      }[],
    ) => selectedRecipients,
  ],
  (clients, companiesMap, selectedRecipients) => {
    const recipientInput: UserCompanySelectorOption[] = [];
    selectedRecipients.forEach((r) => {
      // Handle client selection (with or without company)
      if (r.type === 'client' && r.clientId) {
        const client = clients.get(r.clientId);
        if (client) {
          recipientInput.push({
            value: r.clientId,
            label: `${client.fields.givenName} ${client.fields.familyName}`,
            companyId:
              r.companyId ||
              (client.fields.companyIds?.length === 1
                ? client.fields.companyIds[0]
                : undefined),
            type: 'client',
            avatarSrc: client.fields.avatarImageUrl,
            avatarFallbackColor: client.fields.fallbackColor,
          });
        }
      }
      // Handle company-only selection
      else if (r.type === 'company' && r.companyId && !r.clientId) {
        const company = companiesMap.get(r.companyId);
        if (company) {
          recipientInput.push({
            value: r.companyId,
            label: company.fields.name || '',
            companyId: r.companyId,
            type: 'company',
            avatarSrc: company.fields.avatarImageURL,
            avatarFallbackColor: company.fields.fallbackColor,
          });
        }
      }
    });
    return recipientInput.length > 0 ? recipientInput : undefined;
  },
);

export const RecipientSelector = ({
  label,
  recipientTypes,
  onRecipientSelected,
  name,
  ignoreFilterClientsByCompany = false,
  excludedRecipients = [],
  placeholder = '',
  selectedRecipients = [],
  error,
  helperText,
  limitSelectedOptions,
  isDisabled = false,
  onBlur,
  openOnFocus = false,
  autoFocus = false,
  styles: styleOverrides,
  keepSelectedInList = false,
  listClassName,
}: {
  recipientTypes: RecipientType[];
  onRecipientSelected: React.ComponentProps<
    typeof UserCompanySelector
  >['onChange'];
  name: string;
  ignoreFilterClientsByCompany?: boolean;
  excludedRecipients?: { clientId: string; companyId: string }[];
  placeholder?: string;
  label?: string;
  selectedRecipients?: {
    clientId: string;
    companyId: string;
    type: RecipientType;
  }[];
  error?: boolean;
  helperText?: string;
  limitSelectedOptions?: number;
  isDisabled?: boolean;
  onBlur?: React.FocusEventHandler<HTMLDivElement>;
  openOnFocus?: boolean;
  autoFocus?: boolean;
  styles?: Record<string, any>;
  /**
   * Keep already-selected options in the dropdown (with a highlight) instead of removing them.
   * The design-system selector strips selected options from the list by default, which hides
   * the selection state in the menu. Opt in here to retain the rows for visibility-style UIs.
   */
  keepSelectedInList?: boolean;
  /**
   * Class name forwarded to each option row in the dropdown.
   * Use `data-[selected=true]:bg-*` Tailwind variants to style already-selected rows;
   * the design-system sets `data-selected="true"` on selected option elements.
   * Only meaningful when `keepSelectedInList` is also enabled.
   */
  listClassName?: string;
}) => {
  const { data: config } = useGetPortalConfigQuery();
  const { t } = useTranslation();
  const isCompaniesEnabled = !config?.structFields?.disableCompanies;
  const ignoreCompanies =
    !recipientTypes.includes('company') || !isCompaniesEnabled;
  const {
    clientOptions,
    companyOptions,
    internalUserOptions,
    hiddenCompanyOptionIds,
  } = useAppSelector((state) =>
    selectRecipientComboData(
      state,
      recipientTypes,
      excludedRecipients,
      isCompaniesEnabled,
    ),
  );

  const selectedValues = useAppSelector((state) =>
    selectRecipientValues(state, selectedRecipients),
  );

  const isGrouped = recipientTypes.length > 1;

  // companyOptions is dual-purpose (badge map + selectable company rows). When
  // Billing exposes company rows, hide placeholder/archived badge-only entries
  // from selection without dropping them from the badge map.
  const hiddenCompanyOptionIdSet = new Set(hiddenCompanyOptionIds);
  const companiesById = new Map(
    companyOptions.map((company) => [company.value, company]),
  );
  const filterOption =
    hiddenCompanyOptionIds.length === 0
      ? undefined
      : (option: { data: UserCompanySelectorOption }, inputValue: string) => {
          if (ignoreCompanies && option.data.type === 'company') {
            return false;
          }
          if (
            option.data.type === 'company' &&
            hiddenCompanyOptionIdSet.has(option.data.value)
          ) {
            return false;
          }
          const lowerCaseInputValue = inputValue.toLowerCase();
          const optionLabel = option.data.label?.toLowerCase() ?? '';
          if (option.data.type === 'client') {
            const clientCompany = companiesById.get(
              option.data.companyId ?? '',
            );
            const companyName = clientCompany?.label?.toLowerCase();
            if (companyName?.includes(lowerCaseInputValue)) {
              return true;
            }
          }
          return optionLabel.includes(lowerCaseInputValue);
        };

  return (
    <div>
      {label && <Heading size="sm">{label}</Heading>}
      <UserCompanySelector
        autoFocus={autoFocus}
        customLabels={{
          individualTerm: t(($) => $.client, { count: 1 }),
          individualTermPlural: t(($) => $.client, { count: 2 }),
          groupTerm: t(($) => $.company, { count: 1 }),
          groupTermPlural: t(($) => $.company, { count: 2 }),
        }}
        isDisabled={isDisabled}
        grouped={isGrouped}
        ignoreFilterClientsByCompany={ignoreFilterClientsByCompany}
        ignoreCompanies={ignoreCompanies}
        clientUsers={clientOptions}
        companies={companyOptions}
        limitSelectedOptions={limitSelectedOptions}
        internalUsers={internalUserOptions}
        placeholder={placeholder}
        name={name}
        onChange={onRecipientSelected}
        onBlur={onBlur}
        openMenuOnFocus={openOnFocus}
        hideSelectedOptions={!keepSelectedInList}
        listClassName={listClassName}
        isOptionSelected={(
          option: UserCompanySelectorOption,
          selectValue: readonly UserCompanySelectorOption[],
        ) =>
          selectValue.some(
            (v) => v.value === option.value && v.companyId === option.companyId,
          )
        }
        // this is needed for the menu to be inserted at the body in the html tree to avoid z-index issues.
        // E.g. if this selector is used in a modal, the menu would otherwise be inserted in the modal's
        // root which has a lower z-index than the modal itself and thus the menu would expand the height
        // of the modal instead of opening over it.
        menuPortalTarget={document.body}
        styles={{
          placeholder: (base: Record<string, unknown>) => ({
            ...base,
            color: '#9B9FA3',
            fontSize: 14,
          }),
          menuPortal: (base: Record<string, unknown>) => ({
            ...base,
            zIndex: zIndex.modal,
          }),
          control: (base: Record<string, unknown>) => ({
            ...base,
            // temporary fix to show the error state on the input
            // this should be done in the design system
            ...(error && {
              border: '1px solid #991A00',
              borderRadius: 4,
            }),
          }),
          ...styleOverrides,
        }}
        value={selectedValues}
        // Only override when needed — `undefined` would clobber the DS default.
        {...(filterOption ? { filterOption } : {})}
      />
      {error && (
        <ErrorMessage
          message={helperText || ''}
          className="text-sm flex justify-end"
        />
      )}
    </div>
  );
};
